/**
 * Pure Mermaid `quadrantChart` parser.
 *
 * Recognized lines (any order, after the header):
 *   title <text>
 *   x-axis <low> --> <high>        (the `--> <high>` half is optional)
 *   y-axis <low> --> <high>
 *   quadrant-1..4 <label>          (1 = top-right, numbered counter-clockwise)
 *   <label>: [x, y]                (both coordinates 0–1)
 *
 * Returns `null` only when the block is not a quadrant chart at all. A header
 * with no usable points parses to an empty `points` array, which the component
 * surfaces as an empty visual rather than an unparseable one.
 */

import {
  QuadrantCorner,
  type ParsedQuadrant,
  type QuadrantAxis,
  type QuadrantCorners,
  type QuadrantPoint,
} from './quadrant-types';

const MERMAID_LINE_SEPARATOR = '\n';
const QUADRANT_HEADER_REGEX = /^quadrantChart\b/i;
const TITLE_REGEX = /^title\s+(.+)$/i;
const X_AXIS_REGEX = /^x-axis\s+(.+)$/i;
const Y_AXIS_REGEX = /^y-axis\s+(.+)$/i;
const CORNER_REGEX = /^quadrant-([1-4])\s+(.+)$/i;
// Greedy label so `Ops: EMEA: [0.2, 0.3]` splits at the last colon, matching
// mermaid. The bracketed pair is what separates a point from a `title` line.
const POINT_REGEX = /^(.+):\s*\[\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*\]$/;
const AXIS_END_SEPARATOR_REGEX = /\s*-->\s*/;
const QUOTE_WRAPPER_REGEX = /^"(.*)"$/;
const REGEX_FIRST_CAPTURE_REF = '$1';
const COMMENT_MARKER = '%%';
const AXIS_MIN = 0;
const AXIS_MAX = 1;

enum QuadrantCornerNumber {
  TopRight = '1',
  TopLeft = '2',
  BottomLeft = '3',
  BottomRight = '4',
}

const CORNER_BY_NUMBER: Readonly<Record<string, QuadrantCorner>> = {
  [QuadrantCornerNumber.TopRight]: QuadrantCorner.TopRight,
  [QuadrantCornerNumber.TopLeft]: QuadrantCorner.TopLeft,
  [QuadrantCornerNumber.BottomLeft]: QuadrantCorner.BottomLeft,
  [QuadrantCornerNumber.BottomRight]: QuadrantCorner.BottomRight,
};

const EMPTY_CORNERS: QuadrantCorners = {
  [QuadrantCorner.TopRight]: null,
  [QuadrantCorner.TopLeft]: null,
  [QuadrantCorner.BottomLeft]: null,
  [QuadrantCorner.BottomRight]: null,
};

interface QuadrantParseState {
  title: string | null;
  xAxis: QuadrantAxis | null;
  yAxis: QuadrantAxis | null;
  corners: QuadrantCorners;
  points: QuadrantPoint[];
}

function sanitizeMermaidLine(line: string): string {
  const commentIndex = line.indexOf(COMMENT_MARKER);
  const withoutComment = commentIndex >= 0 ? line.slice(0, commentIndex) : line;

  return withoutComment.trim();
}

function getMermaidLines(raw: string): string[] {
  return raw
    .split(MERMAID_LINE_SEPARATOR)
    .map(sanitizeMermaidLine)
    .filter((line) => line.length > 0);
}

function unquote(value: string): string {
  return value.trim().replace(QUOTE_WRAPPER_REGEX, REGEX_FIRST_CAPTURE_REF).trim();
}

function parseAxis(rawAxis: string): QuadrantAxis | null {
  const [rawStart, rawEnd] = rawAxis.split(AXIS_END_SEPARATOR_REGEX);
  const start = unquote(rawStart ?? '');
  if (start.length === 0) {
    return null;
  }

  const end = unquote(rawEnd ?? '');

  return {
    start,
    end: end.length > 0 ? end : null,
  };
}

function isPlottableCoordinate(value: number): boolean {
  return Number.isFinite(value) && value >= AXIS_MIN && value <= AXIS_MAX;
}

function parsePoint(line: string): QuadrantPoint | null {
  const pointMatch = line.match(POINT_REGEX);
  if (!pointMatch) {
    return null;
  }

  const label = unquote(pointMatch[1] ?? '');
  const x = Number.parseFloat(pointMatch[2] ?? '');
  const y = Number.parseFloat(pointMatch[3] ?? '');

  // An out-of-range coordinate cannot be placed in the 0–1 box, so the point is
  // dropped rather than clamped onto an edge it does not belong on.
  if (label.length === 0 || !isPlottableCoordinate(x) || !isPlottableCoordinate(y)) {
    return null;
  }

  return { label, x, y };
}

function applyCornerLine(input: {
  state: QuadrantParseState;
  cornerNumber: string;
  label: string;
}): QuadrantParseState {
  const corner = CORNER_BY_NUMBER[input.cornerNumber];
  const label = unquote(input.label);
  if (!corner || label.length === 0) {
    return input.state;
  }

  return {
    ...input.state,
    corners: {
      ...input.state.corners,
      [corner]: label,
    },
  };
}

function applyQuadrantLine(input: {
  state: QuadrantParseState;
  line: string;
}): QuadrantParseState {
  const titleMatch = input.line.match(TITLE_REGEX);
  if (titleMatch) {
    return { ...input.state, title: unquote(titleMatch[1] ?? '') || null };
  }

  const cornerMatch = input.line.match(CORNER_REGEX);
  if (cornerMatch) {
    return applyCornerLine({
      state: input.state,
      cornerNumber: cornerMatch[1] ?? '',
      label: cornerMatch[2] ?? '',
    });
  }

  const xAxisMatch = input.line.match(X_AXIS_REGEX);
  if (xAxisMatch) {
    return { ...input.state, xAxis: parseAxis(xAxisMatch[1] ?? '') };
  }

  const yAxisMatch = input.line.match(Y_AXIS_REGEX);
  if (yAxisMatch) {
    return { ...input.state, yAxis: parseAxis(yAxisMatch[1] ?? '') };
  }

  const point = parsePoint(input.line);
  if (!point) {
    return input.state;
  }

  return { ...input.state, points: [...input.state.points, point] };
}

export function parseMermaidQuadrant(raw: string): ParsedQuadrant | null {
  const lines = getMermaidLines(raw);
  const headerIndex = lines.findIndex((line) => QUADRANT_HEADER_REGEX.test(line));
  if (headerIndex === -1) {
    return null;
  }

  const parsedState = lines.slice(headerIndex + 1).reduce<QuadrantParseState>(
    (state, line) => applyQuadrantLine({ state, line }),
    {
      title: null,
      xAxis: null,
      yAxis: null,
      corners: EMPTY_CORNERS,
      points: [],
    },
  );

  return {
    title: parsedState.title,
    xAxis: parsedState.xAxis,
    yAxis: parsedState.yAxis,
    corners: parsedState.corners,
    points: parsedState.points,
  };
}
